/**
 * EspaceRHView — Module RH interne (6 onglets)
 * Accueil | Planning | Conges | Documents | Bulletins | Historique
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import axios from 'axios';
import {
  Loader2, Briefcase, Calendar, PalmtreeIcon, FolderOpen, FileText, History,
  Clock, CheckCircle, XCircle, AlertTriangle, Upload, Download, Eye,
  ChevronLeft, ChevronRight, Plus, Send
} from 'lucide-react';
import config from 'config';

const API = `${config.API_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

const TABS = [
  { key: 'dashboard', label: 'Accueil', icon: Briefcase },
  { key: 'schedule', label: 'Planning', icon: Calendar },
  { key: 'leave', label: 'Conges', icon: PalmtreeIcon },
  { key: 'documents', label: 'Documents', icon: FolderOpen },
  { key: 'payslips', label: 'Bulletins', icon: FileText },
  { key: 'history', label: 'Historique', icon: History },
];

const fmtDate = (iso) => {
  if (!iso) return '--';
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return '--'; }
};

const LEAVE_STATUS = {
  PENDING: { label: 'En attente', cls: 'bg-amber-500/20 text-amber-400' },
  APPROVED: { label: 'Approuve', cls: 'bg-emerald-500/20 text-emerald-400' },
  REJECTED: { label: 'Refuse', cls: 'bg-red-500/20 text-red-400' },
};

export default function EspaceRHView({ user }) {
  const [activeTab, setActiveTab] = useState('dashboard');

  return (
    <div data-testid="espace-rh">
      {/* Tab bar — scrollable */}
      <div className="flex gap-1 bg-[#1A1A1A] rounded-lg p-1 overflow-x-auto no-scrollbar mb-4">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-1 px-2.5 py-2 rounded-md text-[11px] font-medium transition-all whitespace-nowrap flex-shrink-0
                ${activeTab === t.key ? 'bg-[#C9A24D] text-black' : 'text-white/50 hover:text-white/80'}`}
              data-testid={`rh-tab-${t.key}`}>
              <Icon className="w-3.5 h-3.5" />{t.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'dashboard' && <DashboardTab />}
      {activeTab === 'schedule' && <ScheduleTab />}
      {activeTab === 'leave' && <LeaveTab />}
      {activeTab === 'documents' && <DocumentsTab />}
      {activeTab === 'payslips' && <PayslipsTab />}
      {activeTab === 'history' && <HistoryTab />}
    </div>
  );
}

/* ═══ 1. ACCUEIL ═══ */
function DashboardTab() {
  const [data, setData] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [empRes, dashRes] = await Promise.all([
          axios.get(`${API}/employees/me`, { headers: getHeaders() }),
          axios.get(`${API}/employees/me/dashboard`, { headers: getHeaders() }),
        ]);
        setEmployee(empRes.data);
        setData(dashRes.data);
      } catch { /* silent */ }
      setLoading(false);
    };
    fetch();
  }, []);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-3" data-testid="rh-dashboard">
      {/* Employee card */}
      <Card className="bg-gradient-to-br from-[#C9A24D]/10 to-[#161616] border-[#C9A24D]/15">
        <CardContent className="p-4">
          <p className="text-white font-semibold text-sm">{data?.driver_name || employee?.driver_name}</p>
          <p className="text-[#C9A24D] text-xs mt-0.5">Salarie interne</p>
        </CardContent>
      </Card>

      {/* Contract info cards */}
      <div className="grid grid-cols-2 gap-2">
        <InfoCard label="Type contrat" value={employee?.contract_type || 'CDI'} />
        <InfoCard label="Heures/semaine" value={`${employee?.weekly_hours || 35}h`} />
        <InfoCard label="Date debut" value={employee?.start_date || '--'} />
        <InfoCard label="Statut" value={employee?.status === 'ACTIVE' ? 'Actif' : employee?.status || '--'} color={employee?.status === 'ACTIVE' ? 'text-emerald-400' : 'text-amber-400'} />
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-3 gap-2">
        <StatCard label="Jours conges" value={data?.vacation_days_remaining ?? 0} total={data?.vacation_days_total ?? 25} color="text-[#C9A24D]" />
        <StatCard label="Documents" value={data?.documents_count ?? 0} color="text-blue-400" />
        <StatCard label="Bulletins" value={data?.payslips_count ?? 0} color="text-emerald-400" />
      </div>
    </div>
  );
}

/* ═══ 2. PLANNING ═══ */
function ScheduleTab() {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('week'); // week | month
  const [currentDate, setCurrentDate] = useState(new Date());

  const fetchSchedule = useCallback(async () => {
    setLoading(true);
    try {
      const start = getWeekStart(currentDate);
      const end = viewMode === 'week'
        ? new Date(start.getTime() + 6 * 86400000)
        : getMonthEnd(currentDate);
      const startStr = start.toISOString().split('T')[0];
      const endStr = end.toISOString().split('T')[0];
      const res = await axios.get(`${API}/employees/me/schedule?start_date=${startStr}&end_date=${endStr}`, { headers: getHeaders() });
      setShifts(res.data?.shifts || []);
    } catch { /* silent */ }
    setLoading(false);
  }, [currentDate, viewMode]);

  useEffect(() => { fetchSchedule(); }, [fetchSchedule]);

  const navigate = (dir) => {
    const days = viewMode === 'week' ? 7 : 30;
    setCurrentDate(prev => new Date(prev.getTime() + dir * days * 86400000));
  };

  return (
    <div className="space-y-3" data-testid="rh-schedule">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1">
          {['week', 'month'].map(m => (
            <button key={m} onClick={() => setViewMode(m)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${viewMode === m ? 'bg-[#C9A24D] text-black' : 'bg-white/5 text-white/50'}`}
              data-testid={`schedule-view-${m}`}>
              {m === 'week' ? 'Semaine' : 'Mois'}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/60" data-testid="schedule-prev">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-white text-xs font-medium min-w-[100px] text-center">
            {currentDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
          </span>
          <button onClick={() => navigate(1)} className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/60" data-testid="schedule-next">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {loading ? <LoadingSpinner /> : (
        shifts.length === 0 ? (
          <div className="text-center py-8 text-white/30">
            <Calendar className="w-8 h-8 mx-auto mb-2" />
            <p className="text-sm">Aucun shift planifie</p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {shifts.map((s, i) => (
              <div key={s.id || i} className="flex items-center justify-between bg-[#1A1A1A] rounded-xl px-3 py-2.5 border border-white/5" data-testid={`schedule-shift-${i}`}>
                <div>
                  <p className="text-white text-xs font-medium">{formatDayName(s.date)}</p>
                  <p className="text-white/30 text-[10px]">{s.date}</p>
                </div>
                <div className="text-right">
                  <p className="text-[#C9A24D] text-sm font-semibold">{s.shift_start} - {s.shift_end}</p>
                  {s.notes && <p className="text-white/30 text-[10px]">{s.notes}</p>}
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

/* ═══ 3. CONGES ═══ */
function LeaveTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const fetchLeave = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/employees/me/leave`, { headers: getHeaders() });
      setData(res.data);
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchLeave(); }, [fetchLeave]);

  const handleSubmit = async () => {
    if (!startDate || !endDate) { setError('Les dates sont requises'); return; }
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/employees/me/leave`, { start_date: startDate, end_date: endDate, reason }, { headers: getHeaders() });
      setShowForm(false);
      setStartDate(''); setEndDate(''); setReason('');
      fetchLeave();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur');
    }
    setSubmitting(false);
  };

  if (loading) return <LoadingSpinner />;

  const balance = data?.balance || {};

  return (
    <div className="space-y-3" data-testid="rh-leave">
      {/* Balance cards */}
      <div className="grid grid-cols-3 gap-2">
        <StatCard label="Total" value={balance.vacation_days_total ?? 25} color="text-blue-400" />
        <StatCard label="Utilises" value={balance.vacation_days_used ?? 0} color="text-amber-400" />
        <StatCard label="Restants" value={balance.vacation_days_remaining ?? 25} color="text-emerald-400" />
      </div>

      {/* New request button */}
      {!showForm && (
        <Button onClick={() => setShowForm(true)} className="w-full bg-[#C9A24D] hover:bg-[#E6C77A] text-black font-semibold min-h-[44px]" data-testid="new-leave-btn">
          <Plus className="w-4 h-4 mr-2" /> Demander un conge
        </Button>
      )}

      {/* Leave request form */}
      {showForm && (
        <Card className="bg-[#1A1A1A] border-[#C9A24D]/20">
          <CardContent className="p-4 space-y-3">
            <h3 className="text-white font-semibold text-sm">Nouvelle demande</h3>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-white/40 text-xs mb-1 block">Du</label>
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                  className="w-full bg-[#111] border border-white/10 rounded-lg px-2.5 py-2 text-white text-sm focus:outline-none"
                  data-testid="leave-start-date" />
              </div>
              <div>
                <label className="text-white/40 text-xs mb-1 block">Au</label>
                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                  className="w-full bg-[#111] border border-white/10 rounded-lg px-2.5 py-2 text-white text-sm focus:outline-none"
                  data-testid="leave-end-date" />
              </div>
            </div>
            <div>
              <label className="text-white/40 text-xs mb-1 block">Motif</label>
              <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Optionnel"
                className="w-full bg-[#111] border border-white/10 rounded-lg px-2.5 py-2 text-white text-sm focus:outline-none"
                data-testid="leave-reason" />
            </div>
            {error && <p className="text-red-400 text-xs">{error}</p>}
            <div className="flex gap-2">
              <Button onClick={handleSubmit} disabled={submitting || !startDate || !endDate}
                className="flex-1 bg-[#C9A24D] hover:bg-[#E6C77A] text-black text-sm" data-testid="submit-leave-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" />Envoyer</>}
              </Button>
              <Button onClick={() => setShowForm(false)} variant="outline" className="border-white/10 text-white/60 text-sm">Annuler</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Leave requests list */}
      <div className="space-y-1.5">
        {(data?.leave_requests || []).length === 0 ? (
          <p className="text-white/30 text-sm text-center py-6">Aucune demande de conge</p>
        ) : (
          (data?.leave_requests || []).map(l => {
            const s = LEAVE_STATUS[l.status] || LEAVE_STATUS.PENDING;
            return (
              <div key={l.id} className="bg-[#1A1A1A] rounded-xl px-3 py-2.5 border border-white/5" data-testid={`leave-request-${l.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-white text-xs font-medium">{fmtDate(l.start_date)} - {fmtDate(l.end_date)}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${s.cls}`}>{s.label}</span>
                </div>
                {l.reason && <p className="text-white/30 text-[10px]">{l.reason}</p>}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

/* ═══ 4. DOCUMENTS RH ═══ */
function DocumentsTab() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [docType, setDocType] = useState('OTHER');
  const [docLabel, setDocLabel] = useState('');
  const [docFile, setDocFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const fetchDocs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/employees/me/documents`, { headers: getHeaders() });
      setDocuments(res.data?.documents || []);
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleUpload = async () => {
    if (!docFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', docFile);
      formData.append('doc_type', docType);
      formData.append('label', docLabel);
      await axios.post(`${API}/employees/me/documents`, formData, { headers: getHeaders() });
      setShowUpload(false);
      setDocFile(null); setDocLabel(''); setDocType('OTHER');
      fetchDocs();
    } catch (err) { alert(err.response?.data?.detail || 'Erreur upload'); }
    setUploading(false);
  };

  const viewDocument = async (id) => {
    try {
      const res = await axios.get(`${API}/employees/me/documents/${id}/view`, { headers: getHeaders() });
      if (res.data?.url) window.open(res.data.url, '_blank');
    } catch { alert('Erreur'); }
  };

  if (loading) return <LoadingSpinner />;

  const DOC_TYPES = [
    { value: 'SICK_LEAVE', label: 'Arret de travail' },
    { value: 'ADMIN_DOC', label: 'Document administratif' },
    { value: 'OTHER', label: 'Autre' },
  ];

  return (
    <div className="space-y-3" data-testid="rh-documents">
      {!showUpload && (
        <Button onClick={() => setShowUpload(true)} className="w-full bg-[#C9A24D] hover:bg-[#E6C77A] text-black font-semibold min-h-[44px]" data-testid="upload-rh-doc-btn">
          <Upload className="w-4 h-4 mr-2" /> Envoyer un document
        </Button>
      )}

      {showUpload && (
        <Card className="bg-[#1A1A1A] border-[#C9A24D]/20">
          <CardContent className="p-4 space-y-3">
            <h3 className="text-white font-semibold text-sm">Nouveau document</h3>
            <div>
              <label className="text-white/40 text-xs mb-1 block">Type</label>
              <select value={docType} onChange={e => setDocType(e.target.value)}
                className="w-full bg-[#111] border border-white/10 rounded-lg px-2.5 py-2 text-white text-sm focus:outline-none"
                data-testid="rh-doc-type">
                {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-white/40 text-xs mb-1 block">Libelle</label>
              <input value={docLabel} onChange={e => setDocLabel(e.target.value)} placeholder="Optionnel"
                className="w-full bg-[#111] border border-white/10 rounded-lg px-2.5 py-2 text-white text-sm focus:outline-none"
                data-testid="rh-doc-label" />
            </div>
            <div>
              <label className="flex items-center gap-2 px-3 py-2.5 bg-[#111] border border-white/10 rounded-lg cursor-pointer hover:border-[#C9A24D]/30 transition">
                <Upload className="w-4 h-4 text-white/40" />
                <span className="text-white/60 text-sm truncate">{docFile ? docFile.name : 'Choisir un fichier...'}</span>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden"
                  onChange={e => setDocFile(e.target.files[0] || null)} data-testid="rh-doc-file" />
              </label>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleUpload} disabled={uploading || !docFile}
                className="flex-1 bg-[#C9A24D] hover:bg-[#E6C77A] text-black text-sm" data-testid="submit-rh-doc-btn">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Envoyer'}
              </Button>
              <Button onClick={() => setShowUpload(false)} variant="outline" className="border-white/10 text-white/60 text-sm">Annuler</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Documents list */}
      {documents.length === 0 ? (
        <div className="text-center py-8 text-white/30">
          <FolderOpen className="w-8 h-8 mx-auto mb-2" />
          <p className="text-sm">Aucun document</p>
        </div>
      ) : (
        documents.map(d => (
          <div key={d.id} className="flex items-center justify-between bg-[#1A1A1A] rounded-xl px-3 py-2.5 border border-white/5" data-testid={`rh-doc-${d.id}`}>
            <div className="min-w-0 flex-1">
              <p className="text-white text-xs font-medium truncate">{d.label || d.type}</p>
              <p className="text-white/30 text-[10px]">{fmtDate(d.created_at)} - {d.uploaded_by === 'admin' ? 'Admin' : 'Vous'}</p>
            </div>
            <button onClick={() => viewDocument(d.id)} className="text-[#C9A24D] text-xs flex items-center gap-1 hover:text-[#E6C77A] ml-2 shrink-0" data-testid={`view-doc-${d.id}`}>
              <Eye className="w-3.5 h-3.5" /> Voir
            </button>
          </div>
        ))
      )}
    </div>
  );
}

/* ═══ 5. BULLETINS ═══ */
function PayslipsTab() {
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/employees/me/payslips`, { headers: getHeaders() });
        setPayslips(res.data?.payslips || []);
      } catch { /* silent */ }
      setLoading(false);
    };
    fetch();
  }, []);

  const viewPayslip = async (id) => {
    try {
      const res = await axios.get(`${API}/employees/me/payslips/${id}/view`, { headers: getHeaders() });
      if (res.data?.url) window.open(res.data.url, '_blank');
    } catch { alert('Erreur'); }
  };

  const downloadPayslip = async (id) => {
    try {
      const res = await axios.get(`${API}/employees/me/payslips/${id}/download`, { headers: getHeaders() });
      if (res.data?.url) window.open(res.data.url, '_blank');
    } catch { alert('Erreur'); }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-2" data-testid="rh-payslips">
      {payslips.length === 0 ? (
        <div className="text-center py-8 text-white/30">
          <FileText className="w-8 h-8 mx-auto mb-2" />
          <p className="text-sm">Aucun bulletin de salaire</p>
        </div>
      ) : (
        payslips.map(p => (
          <div key={p.id} className="flex items-center justify-between bg-[#1A1A1A] rounded-xl px-3 py-2.5 border border-white/5" data-testid={`payslip-${p.id}`}>
            <div>
              <p className="text-white text-xs font-medium">{p.month_label} {p.year}</p>
              <p className="text-white/30 text-[10px]">{fmtDate(p.created_at)}</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => viewPayslip(p.id)} className="text-[#C9A24D] text-xs flex items-center gap-1 hover:text-[#E6C77A]" data-testid={`view-payslip-${p.id}`}>
                <Eye className="w-3.5 h-3.5" /> Voir
              </button>
              <button onClick={() => downloadPayslip(p.id)} className="text-blue-400 text-xs flex items-center gap-1 hover:text-blue-300" data-testid={`download-payslip-${p.id}`}>
                <Download className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

/* ═══ 6. HISTORIQUE ═══ */
function HistoryTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('week');
  const [currentDate, setCurrentDate] = useState(new Date());

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const start = viewMode === 'week' ? getWeekStart(currentDate) : getMonthStart(currentDate);
      const end = viewMode === 'week'
        ? new Date(start.getTime() + 6 * 86400000)
        : viewMode === 'month' ? getMonthEnd(currentDate) : getYearEnd(currentDate);
      const startStr = start.toISOString().split('T')[0];
      const endStr = end.toISOString().split('T')[0];
      const res = await axios.get(`${API}/employees/me/history?start_date=${startStr}&end_date=${endStr}`, { headers: getHeaders() });
      setData(res.data);
    } catch { /* silent */ }
    setLoading(false);
  }, [currentDate, viewMode]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const navigate = (dir) => {
    const days = viewMode === 'week' ? 7 : viewMode === 'month' ? 30 : 365;
    setCurrentDate(prev => new Date(prev.getTime() + dir * days * 86400000));
  };

  return (
    <div className="space-y-3" data-testid="rh-history">
      {/* View toggle + navigation */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1">
          {['week', 'month', 'year'].map(m => (
            <button key={m} onClick={() => setViewMode(m)}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition ${viewMode === m ? 'bg-[#C9A24D] text-black' : 'bg-white/5 text-white/50'}`}
              data-testid={`history-view-${m}`}>
              {m === 'week' ? 'Sem.' : m === 'month' ? 'Mois' : 'Annee'}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/60" data-testid="history-prev">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={() => navigate(1)} className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/60" data-testid="history-next">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {loading ? <LoadingSpinner /> : (
        <>
          {/* Summary stats */}
          {data?.summary && (
            <div className="grid grid-cols-3 gap-2">
              <StatCard label="Heures" value={`${data.summary.total_hours}h`} color="text-[#C9A24D]" />
              <StatCard label="Missions" value={data.summary.mission_count} color="text-blue-400" />
              <StatCard label="Jours" value={data.summary.days_worked} color="text-emerald-400" />
            </div>
          )}

          {/* Entries */}
          {(data?.entries || []).length === 0 ? (
            <div className="text-center py-8 text-white/30">
              <History className="w-8 h-8 mx-auto mb-2" />
              <p className="text-sm">Aucun historique</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {(data?.entries || []).map((e, i) => (
                <div key={e.id || i} className="flex items-center justify-between bg-[#1A1A1A] rounded-xl px-3 py-2.5 border border-white/5" data-testid={`history-entry-${i}`}>
                  <div>
                    <p className="text-white text-xs font-medium">{formatDayName(e.date)}</p>
                    <p className="text-white/30 text-[10px]">{e.date}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[#C9A24D] text-sm font-semibold">{e.shift_start} - {e.shift_end}</p>
                    <p className="text-white/30 text-[10px]">{e.total_hours}h{e.notes ? ` - ${e.notes}` : ''}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ═══ Shared Components ═══ */

function LoadingSpinner() {
  return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-[#C9A24D] animate-spin" /></div>;
}

function InfoCard({ label, value, color }) {
  return (
    <Card className="bg-[#1A1A1A] border-white/5">
      <CardContent className="p-3 text-center">
        <p className="text-white/40 text-[10px]">{label}</p>
        <p className={`text-sm font-semibold mt-0.5 ${color || 'text-white'}`}>{value}</p>
      </CardContent>
    </Card>
  );
}

function StatCard({ label, value, total, color }) {
  return (
    <Card className="bg-[#1A1A1A] border-white/5">
      <CardContent className="p-3 text-center">
        <p className="text-white/40 text-[10px]">{label}</p>
        <p className={`text-lg font-bold ${color || 'text-white'}`}>
          {value}{total !== undefined ? <span className="text-white/20 text-xs font-normal">/{total}</span> : ''}
        </p>
      </CardContent>
    </Card>
  );
}

/* ═══ Helpers ═══ */

function getWeekStart(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
}

function getMonthStart(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function getMonthEnd(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function getYearEnd(date) {
  return new Date(date.getFullYear(), 11, 31);
}

function formatDayName(dateStr) {
  if (!dateStr) return '--';
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short' });
  } catch { return dateStr; }
}
